// Helper murni keranjang pra-checkout multi-produk (tanpa React) — bisa diuji
// tanpa JSX runtime. Bentuk data: tiap entri `CartState` = SATU produk + banyak
// line varian. Keranjang selalu boleh menampung banyak produk; saat checkout
// tiap produk tetap dibuat sebagai order TERPISAH (1 order = 1 produk di DB).

import { computeOrderDpTotal, type DpMode } from "@/lib/dp";
import {
  ORDERABLE_READY_STOCK_STATUSES,
  type Product,
  type ProductVariation,
} from "@/lib/products";

export type CartLine = {
  variationId: string;
  variationName: string;
  unitPrice?: number;
  /**
   * Tambahan (mig 7900) yang TERMASUK di `unitPrice` saat masuk keranjang —
   * pasangannya, dipakai jalur cadangan checkout (varian gagal dimuat) supaya
   * harga keranjang lama tak ditempeli tambahan HARI INI (bughunt D14-31).
   * Absen = keranjang lama / harga menyusul → tak diketahui (trigger DB).
   */
  priceExtra?: number;
  qty: number;
  /** Catatan pembeli per varian (mis. nama untuk personalisasi), maks 20 char. */
  note?: string;
};

/** Batas panjang catatan per varian (storefront & admin, cermin DB `item_note`). */
export const MAX_LINE_NOTE_LENGTH = 20;

export type CartState = {
  productId: string;
  productSlug: string;
  productName: string;
  productImage: string;
  estimatedReady: string;
  preorderStatus: string;
  /** PO wajib DP? (dari Product.dpRequired) */
  dpRequired?: boolean;
  /** Nilai DP — maknanya tergantung dpMode (dari Product.dpAmount) */
  dpAmount?: number;
  /** Mode DP produk (dari Product.dpMode, mig 070) */
  dpMode?: DpMode;
  /**
   * PO berkuota? (dari `Product.poLimited`, mig 3970) — potret saat item masuk
   * keranjang, idiom yang sama dengan `dpRequired`/`dpAmount` di atas.
   *
   * Dipakai keranjang untuk memutuskan apakah perlu menanyakan sisa kuota ke
   * server; produk PO biasa (mayoritas) tetap nol round-trip. Keranjang lama di
   * localStorage tidak punya kunci ini → `undefined` → diperlakukan TIDAK
   * berkuota, yaitu persis perilaku sebelum ini: gerbang otoritatifnya tetap
   * trigger DB saat checkout.
   */
  poLimited?: boolean;
  /**
   * Tipe produk (dari `Product.productType`) — menentukan sub-kolom halaman
   * Pesanan tempat barang mendarat setelah checkout (ready_stock auto-keep →
   * "Sudah Ready"; PO → "Belum Ready"). Keranjang lama di localStorage tidak
   * punya kunci ini → `cartLandingSubTab` menebak dari snapshot status.
   */
  productType?: "preorder" | "ready_stock";
  lines: CartLine[];
};

/** Normalisasi catatan line: trim + cap 20 char; kosong → undefined. */
export function normalizeLineNote(note?: string): string | undefined {
  const normalized = note?.trim().slice(0, MAX_LINE_NOTE_LENGTH);
  return normalized || undefined;
}

function normalizeOneCart(raw: unknown): CartState | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.productId !== "string" || !obj.productId) return null;
  const lines = Array.isArray(obj.lines) ? (obj.lines as CartLine[]) : [];
  return {
    ...(obj as unknown as CartState),
    lines: lines.map((line) => ({
      ...line,
      note: normalizeLineNote(line.note),
    })),
  };
}

/**
 * Petakan nilai tersimpan (localStorage) → array keranjang. Menerima KETIGA
 * bentuk demi kompat-mundur:
 *   • array `CartState[]` (bentuk baru),
 *   • objek `{ carts: CartState[] }`,
 *   • objek `CartState` tunggal (bentuk lama single-produk).
 * Bentuk rusak / tak dikenal → `[]`.
 */
export function normalizeSavedCarts(raw: unknown): CartState[] {
  if (Array.isArray(raw)) {
    return raw.map(normalizeOneCart).filter((c): c is CartState => c !== null);
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.carts)) {
      return obj.carts
        .map(normalizeOneCart)
        .filter((c): c is CartState => c !== null);
    }
    const single = normalizeOneCart(raw);
    return single ? [single] : [];
  }
  return [];
}

/**
 * Tambah/akumulasi satu varian ke keranjang: akumulasi ke entri produk yang
 * cocok, atau push entri baru untuk produk yang belum ada.
 * Fungsi murni: mengembalikan array baru; tidak memutasi input.
 */
export function addProductToCarts(
  carts: CartState[],
  product: Product,
  variation: ProductVariation,
  qty: number,
  unitPrice: number | undefined,
  /** Tambahan yang termasuk di `unitPrice` (mig 7900); absen = tak diketahui. */
  priceExtra?: number
): CartState[] {
  const existingIdx = carts.findIndex((c) => c.productId === product.id);
  // Pasangan (harga, tambahan) — tambahan hanya berarti bersama harganya.
  const extraKey = unitPrice !== undefined && priceExtra !== undefined ? { priceExtra } : {};

  if (existingIdx === -1) {
    const entry: CartState = {
      productId: product.id,
      productSlug: product.slug,
      productName: product.name,
      productImage: product.images[0],
      estimatedReady: product.estimatedReady,
      preorderStatus: product.status,
      dpRequired: product.dpRequired ?? false,
      dpAmount: product.dpAmount,
      dpMode: product.dpMode ?? "order",
      poLimited: product.poLimited ?? false,
      productType: product.productType,
      lines: [
        {
          variationId: variation.id,
          variationName: variation.name,
          unitPrice,
          ...extraKey,
          qty,
        },
      ],
    };
    return [...carts, entry];
  }

  const target = carts[existingIdx];
  const lineIdx = target.lines.findIndex(
    (l) => l.variationId === variation.id
  );
  const nextLines =
    lineIdx === -1
      ? [
          ...target.lines,
          {
            variationId: variation.id,
            variationName: variation.name,
            unitPrice,
            ...extraKey,
            qty,
          },
        ]
      : target.lines.map((l, i) =>
          i === lineIdx ? { ...l, qty: l.qty + qty } : l
        );

  return carts.map((c, i) =>
    i === existingIdx ? { ...c, lines: nextLines } : c
  );
}

// Entri target: produk tertentu bila `productId` diberi; selain itu entri pertama
// (kompat pemanggil lama mode single). -1 = tak ada target.
function resolveTargetIdx(
  carts: CartState[],
  productId: string | undefined
): number {
  if (productId) return carts.findIndex((c) => c.productId === productId);
  return carts.length > 0 ? 0 : -1;
}

/**
 * Ubah qty satu line. qty ≤ 0 menghapus line; bila entri produk jadi tanpa line,
 * entri itu dibuang dari array.
 */
export function updateLineInCarts(
  carts: CartState[],
  productId: string | undefined,
  variationId: string,
  qty: number
): CartState[] {
  const idx = resolveTargetIdx(carts, productId);
  if (idx === -1) return carts;

  const nextLines = carts[idx].lines
    .map((l) => (l.variationId === variationId ? { ...l, qty } : l))
    .filter((l) => l.qty > 0);

  if (nextLines.length === 0) {
    return carts.filter((_, i) => i !== idx);
  }
  return carts.map((c, i) => (i === idx ? { ...c, lines: nextLines } : c));
}

/**
 * Ubah catatan satu line SAAT MENGETIK: hanya cap panjang, TANPA trim.
 * (trim di sini akan membuang spasi trailing pada TIAP keystroke — spasi
 * yang baru diketik selalu jadi karakter trailing sesaat sebelum karakter
 * berikutnya, jadi trim-per-keystroke membuatnya mustahil mengetik spasi
 * sama sekali. Trim akhir dilakukan saat submit/checkout, bukan di sini.)
 */
export function updateNoteInCarts(
  carts: CartState[],
  productId: string | undefined,
  variationId: string,
  note: string
): CartState[] {
  const idx = resolveTargetIdx(carts, productId);
  if (idx === -1) return carts;
  const capped = note.slice(0, MAX_LINE_NOTE_LENGTH);
  return carts.map((c, i) =>
    i === idx
      ? {
          ...c,
          lines: c.lines.map((l) =>
            l.variationId === variationId ? { ...l, note: capped } : l
          ),
        }
      : c
  );
}

/** Buang satu entri produk dari keranjang. */
export function clearProductFromCarts(
  carts: CartState[],
  productId: string
): CartState[] {
  return carts.filter((c) => c.productId !== productId);
}

/** Total qty lintas semua produk. */
export function cartTotalQty(carts: CartState[]): number {
  return carts.reduce(
    (sum, c) => sum + c.lines.reduce((s, l) => s + l.qty, 0),
    0
  );
}

/**
 * Subtotal lintas semua produk. Cermin semantik lama: `null` bila ADA satu line
 * mana pun tanpa harga ("harga menyusul"), atau keranjang kosong.
 */
export function cartSubtotal(carts: CartState[]): number | null {
  const lines = carts.flatMap((c) => c.lines);
  if (lines.length === 0) return null;
  if (!lines.every((l) => typeof l.unitPrice === "number")) return null;
  return lines.reduce((s, l) => s + (l.unitPrice as number) * l.qty, 0);
}

/** Total DP satu produk menurut mode produk (mig 070). */
export function cartDpTotal(cart: CartState): number {
  return computeOrderDpTotal(
    cart.dpMode,
    cart.dpAmount ?? 0,
    cart.lines.map((l) => ({ qty: l.qty, unitPrice: l.unitPrice }))
  );
}

/** Total DP lintas semua produk. */
export function cartDpTotalAll(carts: CartState[]): number {
  return carts.reduce((sum, c) => sum + cartDpTotal(c), 0);
}

/** Sub-kolom halaman Pesanan (`/orders?tab=keranjang&sub=…`). */
export type OrdersLandingSubTab = "ready" | "belum_ready";

// Entri ini mendarat di "Sudah Ready"? ready_stock di-keep otomatis oleh
// trigger saat order (mig 031); item PO menunggu proses di "Belum Ready".
// Keranjang lama tanpa `productType` ditebak dari snapshot status: hanya
// ready_stock yang orderable pada status READY (isOrderableStatus, mig 090).
function landsOnReady(cart: CartState): boolean {
  if (cart.productType) return cart.productType === "ready_stock";
  return ORDERABLE_READY_STOCK_STATUSES.some(
    (s) => s === cart.preorderStatus
  );
}

/**
 * Sub-kolom tujuan setelah "Keep Produk": semua PO → `belum_ready`;
 * ada ready_stock → `ready` — untuk campuran keduanya ikut default pintar
 * OrdersClient (Sudah Ready dulu: kolom yang langsung bisa dibayar/dikirim,
 * dan keep ready stock punya tenggat hangus).
 */
export function cartLandingSubTab(carts: CartState[]): OrdersLandingSubTab {
  return carts.some(landsOnReady) ? "ready" : "belum_ready";
}

// ─── BUNDEL (mig 8040) — tambahan murni, tak mengubah helper di atas ────────

/**
 * Total DP satu produk bila keranjang JUGA memuat komponen bundel produk ini:
 * pesanan PO satu produk menampung baris biasa DAN baris bundelnya (RPC
 * `checkout_orders_with_bundles` menggabungkannya), jadi DP per pesanan
 * menghitung keduanya. `extra` = baris komponen (qty = jumlah bundel, harga =
 * harga bundel per pcs). Tanpa `extra` = `cartDpTotal` persis. `null` = tak
 * bisa dihitung PASTI (mode persen dengan harga baris bundel belum diketahui)
 * — pemanggil tidak memajang angka, bukan memajang angka yang salah.
 */
export function cartDpTotalWithExtraLines(
  cart: CartState,
  extra: ReadonlyArray<{ qty: number; unitPrice?: number | null }>
): number | null {
  if (!extra.length) return cartDpTotal(cart);
  if (
    cart.dpMode === "percent" &&
    extra.some((l) => typeof l.unitPrice !== "number" || !Number.isFinite(l.unitPrice))
  ) {
    return null;
  }
  return computeOrderDpTotal(cart.dpMode, cart.dpAmount ?? 0, [
    ...cart.lines.map((l) => ({ qty: l.qty, unitPrice: l.unitPrice })),
    ...extra.map((l) => ({ qty: l.qty, unitPrice: l.unitPrice ?? null }))
  ]);
}

/**
 * `cartLandingSubTab` untuk keranjang yang juga memuat bundel: bundel Ready
 * Stock mendarat di "Sudah Ready" (keep otomatis), bundel PO di "Belum Ready".
 * Tanpa bundel = `cartLandingSubTab` persis.
 */
export function cartLandingSubTabWithBundles(
  carts: CartState[],
  bundles: ReadonlyArray<{ productType: "preorder" | "ready_stock" }>
): OrdersLandingSubTab {
  if (bundles.some((b) => b.productType === "ready_stock")) return "ready";
  return cartLandingSubTab(carts);
}
